/**
 * Per-node render inputs for the System roll-up diagram (Flow L Frame 1;
 * ADR-035 / S6). Pure, React-free.
 *
 * Two concerns, both of which have to be computable *without* a DOM:
 *
 *   1. :func:`resolveNodeRender` — the single ``{ state, signal }`` a node
 *      displays, reduced from a :type:`TopologyNode` + the kind-grouped merged
 *      snapshot. Shared by the topology diagram (Frame 1) and the relocated
 *      **Entity Details** detail (ADR-024 §4/§5 amended 2026-07-24) so both
 *      surfaces compute the *same* stoplight for a given node — a status pill
 *      in Entity Details disagreeing with the topology it was selected from is
 *      exactly the inconsistency operator-visibility rules out (CLAUDE.md §8.1;
 *      one focus per REQ-UI-051).
 *   2. :func:`nodeLabelLines` / :func:`layoutGlyphLabel` — the v2 canvas label
 *      rule (REQ-STATUS-046): the label is centred in its shape and the status
 *      glyph sits to its **left**, with the glyph+label group centred as a
 *      whole. Kept here rather than in the ``.tsx`` because it is arithmetic,
 *      not markup: it is unit-testable, and ``react-refresh`` allows only
 *      constant exports beside a component.
 *
 * # Why the layout is estimated, not measured
 *
 * The storyboard mock measures each label with ``getComputedTextLength()`` and
 * falls back to a deterministic estimate when the node is detached. A React
 * transcription cannot measure during render (jsdom returns 0, and a real
 * browser would need a second layout pass and an effect), so this module uses
 * **the mock's own documented fallback** — ``estLabelW`` — as the primary rule.
 * That keeps the composition pure and the geometry assertable in a unit test;
 * the ±1-unit optical difference against a measured centre is invisible at the
 * diagram's fit scale, and the §5.11 visual pass is what confirms it.
 *
 * Cross-references:
 *   - Flow L §3 (roll-up view); mock header §1/§3 (the coordinate source of
 *     record); REQ-UI-046 (status palette); REQ-STATUS-016 (never a false
 *     green); REQ-STATUS-046 (centred labels, no COTS/process/edge labels).
 *   - ./status-topology (:data:`TOPOLOGY_NODES`, :type:`TopologyNode`).
 */
import {
  componentInlineSignal,
  nodeRollupState,
  parseRollupState,
  type ComponentKind,
  type ResolvedComponent,
  type RollupState,
} from "@/system-status/status-model";
import type { TopologyNode } from "@/system-status/status-topology";

// ---------------------------------------------------------------------------
// Stoplight resolution
// ---------------------------------------------------------------------------

/** The resolved render inputs for one node — state + the inline summary signal. */
export interface ResolvedNodeRender {
  state: RollupState;
  signal: string | null;
}

/**
 * Resolve one node's ``{ state, signal }`` from the kind-grouped merged
 * snapshot⊕stream components (:func:`groupByKind`).
 *
 * A v2 node names a **set** of backing kinds (:prop:`TopologyNode.kinds`), so
 * the rollup is worst-of across *every* one of them, not just the first. That
 * matters on the two consolidated nodes: Egress covers all three egress kinds
 * and Provider Manager covers every ingress kind, and reducing over only the first would
 * paint the whole egress side green while a raw interface was failing
 * (REQ-STATUS-016 — never a false green).
 *
 * A node whose kinds the snapshot does not report — including the planned
 * Tasking Manager, which declares none — resolves to ``unknown`` (honest grey).
 */
export function resolveNodeRender(
  node: TopologyNode,
  byKind: ReadonlyMap<ComponentKind, readonly ResolvedComponent[]>,
): ResolvedNodeRender {
  const group = node.kinds.flatMap((kind) => [...(byKind.get(kind) ?? [])]);
  const state = nodeRollupState(group);
  // The instance the signal describes is the one whose rollup WON the node's
  // stoplight, not simply the first reported (Flow L §3). `state` is reduced
  // from this same list, so a non-empty group always has a match; an empty one
  // has nothing honest to say and says nothing.
  const worst = group.find((c) => parseRollupState(c.rollup_state) === state);
  return { state, signal: worst === undefined ? null : componentInlineSignal(worst) };
}

// ---------------------------------------------------------------------------
// Canvas label geometry (REQ-STATUS-046)
// ---------------------------------------------------------------------------

/** Font size of a component's canvas label (mock ``glyphLabel(… , 13)``). */
export const NODE_LABEL_FONT_SIZE = 13;

/** Baseline pitch between the two lines of a wrapped title (mock ``LINE_H``). */
export const NODE_LABEL_LINE_HEIGHT = 15;

/** Distance from the glyph's right edge to the first label pixel (mock ``GLYPH_GAP``). */
export const GLYPH_LABEL_GAP = 7;

/** Status-glyph radius on a component (mock ``NODE_GLYPH_R``). */
export const NODE_GLYPH_RADIUS = 6;

/** Status-glyph radius inside an interface icon (mock ``IFACE_GLYPH_R``). */
export const INTERFACE_GLYPH_RADIUS = 4;

/**
 * Mean advance width of one character, as a fraction of the font size, for the
 * UI font stack at diagram sizes (mock ``estLabelW``: ~0.505 em). Capitals and
 * digits run a little wider, lowercase a little narrower.
 */
const LABEL_ADVANCE_RATIO = 0.505;

/**
 * Character budget above which an *undeclared* node title wraps
 * (:func:`nodeLabelLines` fallback).
 *
 * Chosen so the fallback reproduces the mock's declared wraps exactly:
 * ``Message Bus`` (11) stays on one line and ``Raw Data Store`` (14) breaks.
 */
const NODE_LABEL_WRAP_THRESHOLD = 12;

/**
 * Deterministic label-width estimate — the mock's ``estLabelW``, and the rule
 * it names for "a non-DOM transcription of this file".
 */
export function estimateLabelWidth(text: string, fontSize: number): number {
  return text.length * fontSize * LABEL_ADVANCE_RATIO;
}

/**
 * How each composed node's title breaks across lines, transcribed verbatim from
 * the storyboard mock's per-node ``lines`` declarations (mock §4 d — titles wrap
 * onto two lines because this diagram lives in a docked workspace panel and
 * still has to reach the Egress interface stack at the right end).
 *
 * The wrap is **declared, not computed**, exactly as the mock declares it: the
 * break point is a composition decision the drawing owns, and a computed wrap
 * would silently re-break every title the next time a name changed.
 *
 * ⚠ This table lives in the renderer only because :type:`TopologyNode` carries
 * no ``lines`` field. It belongs beside the node's ``name`` in
 * ``status-topology.ts`` — moving it there is the right cleanup the moment that
 * type gains the field, and the "covers every composed node" test is what keeps
 * the two in step until then.
 */
const NODE_LABEL_LINES: Readonly<Record<string, readonly string[]>> = {
  "user-interface-manager": ["User Interface", "Manager"],
  "tasking-manager": ["Tasking", "Manager"],
  "time-series-manager": ["Time Series", "Manager"],
  // "Processed" / "Data Store" rather than "Processed Data" / "Store" (architect,
  // 2026-08-01): the break falls between the qualifier and the noun phrase, so the
  // second line names the thing the node *is*. Matches "Raw Data Store" below,
  // whose two lines read the same way.
  "processed-data-store": ["Processed", "Data Store"],
  "provider-manager": ["Provider", "Manager"],
  "message-bus": ["Message Bus"],
  "peer-manager": ["Peer", "Manager"],
  "raw-data-store": ["Raw Data", "Store"],
};

/**
 * The label lines a node draws — its declared wrap where one exists, otherwise a
 * deterministic last-space break above :data:`NODE_LABEL_WRAP_THRESHOLD`.
 *
 * The fallback exists so a node added to the topology ahead of this table still
 * renders a readable title rather than one long string overflowing its
 * rectangle; it is a safety net, not the mechanism (see :data:`NODE_LABEL_LINES`).
 */
export function nodeLabelLines(node: TopologyNode): readonly string[] {
  const declared = NODE_LABEL_LINES[node.id];
  if (declared !== undefined) return declared;
  if (node.name.length <= NODE_LABEL_WRAP_THRESHOLD) return [node.name];
  const split = node.name.lastIndexOf(" ");
  // A long single-word name has nowhere to break; one over-wide line is honest,
  // a mid-word hyphenation would not be.
  if (split <= 0) return [node.name];
  return [node.name.slice(0, split), node.name.slice(split + 1)];
}

/** Resolved positions for one glyph + centred multi-line label group. */
export interface GlyphLabelLayout {
  /** Centre x of the status glyph. */
  glyphCx: number;
  /** Shared x of every label line (each drawn with ``text-anchor="middle"``). */
  labelX: number;
  /** Centre y of each label line, in order (drawn ``dominant-baseline="central"``). */
  lineYs: readonly number[];
  /** Estimated width of the widest line — the label block's own width. */
  labelWidth: number;
}

/**
 * Lay out a status glyph to the **left** of a one- or two-line label, with the
 * glyph+label group centred as a whole on ``(cx, cy)`` (REQ-STATUS-046; mock
 * ``layoutGlyphLabel``).
 *
 * The group's width is driven by the **widest** line so neither line overhangs
 * the glyph's optical column. With no label at all the glyph alone is centred —
 * the inter-item gap is not charged for an item that is not there, which is what
 * keeps a hypothetical unlabelled shape from drifting half a gap off centre.
 */
export function layoutGlyphLabel(
  lines: readonly string[],
  cx: number,
  cy: number,
  radius: number,
  fontSize: number,
): GlyphLabelLayout {
  const labelWidth =
    lines.length === 0 ? 0 : Math.max(...lines.map((line) => estimateLabelWidth(line, fontSize)));
  const groupWidth = 2 * radius + (lines.length === 0 ? 0 : GLYPH_LABEL_GAP + labelWidth);
  const left = cx - groupWidth / 2;
  return {
    glyphCx: left + radius,
    labelX: left + 2 * radius + GLYPH_LABEL_GAP + labelWidth / 2,
    lineYs: lines.map((_, i) => cy + (i - (lines.length - 1) / 2) * NODE_LABEL_LINE_HEIGHT),
    labelWidth,
  };
}

// ---------------------------------------------------------------------------
// Selection / containment / focus geometry (REQ-STATUS-059..-061)
// ---------------------------------------------------------------------------

/**
 * Offset, dash rhythm and width of the **containment** ring — the treatment on
 * an Ingress/Peer Manager node while one of *its* interface icons is the inspected
 * entity (REQ-STATUS-061).
 *
 * Deliberately *outside* the node and dashed, so it reads as "related, not it":
 * selection is the node's own edge restroked (REQ-STATUS-059 amended
 * 2026-08-01), and there is no longer a ring count to tell the two apart.
 */
export const CONTAINMENT_RING_OFFSET = 5;
export const CONTAINMENT_RING_DASH = "6 4";
export const CONTAINMENT_RING_WIDTH = 1.5;

/**
 * The **keyboard-focus** ring on a component node. Both the offset and the dash
 * rhythm differ from containment's, so focus, containment and selection are
 * three distinguishable treatments rather than one repeated at three radii
 * (REQ-STATUS-061). Never suppressed in favour of selection.
 */
export const FOCUS_RING_OFFSET = 11;
export const FOCUS_RING_DASH = "2 3";
export const FOCUS_RING_WIDTH = 1.5;

/**
 * The focus ring offset on an **interface icon**. Much tighter than a node's:
 * the icon pitch is 22 against an 18-unit box, so a wide ring would collide with
 * its neighbours' rows. Selection on an icon is still its own edge in ``--ring``,
 * so solid-on-the-edge versus fine-dashed-outside keeps the two apart at this
 * size too.
 */
export const INTERFACE_FOCUS_RING_OFFSET = 2;

/** Stroke width of the single selection outline (REQ-STATUS-059 amended). */
export const SELECTION_STROKE_WIDTH = 2;

/** Label weight when selected, and when not (REQ-STATUS-059 — not optional). */
export const SELECTED_LABEL_WEIGHT = 700;
export const LABEL_WEIGHT = 500;
